// Service imports:
import { getCauseTypeOfAlert } from './alertUtils';
// Type imports:
import { AlertStatus } from '../types/api/AlertTypes';
import { TickData, WithBook } from '../types/api/Types';
import { isWithBookObject } from '../types/api/Types';
import { PublicOrderTickData } from '../types/api/PublicTypes';
import { IPrivateOrder } from '../types/api/PrivateTypes';

interface IIntraTickSortObject {
    tickdata_type: "Order" | "Trade" | "Alert" | "News" | "Bookmark" | "PrivateOrder" | "PrivateTrade" | "PrivateTradeReport" | "Order By Level",
    intraTicksortValue: string | null
}

const getTickSortValue = (tick: TickData): IIntraTickSortObject | null => {
    switch(tick.tickdata_type) {
        case 'Bookmark':
            return {
                tickdata_type: 'Bookmark',
                intraTicksortValue: tick.id.toString()
            };
        case 'Order':
            return {
                tickdata_type: 'Order',
                intraTicksortValue: tick.order_id
            };
        case 'Order By Level':
            return {
                tickdata_type: 'Order By Level',
                intraTicksortValue: tick.id?.toString() ?? null
            };
        case 'Trade':
            return {
                tickdata_type: 'Trade',
                intraTicksortValue: tick.trade_number?.toString() ?? null
            };
        case 'News':
            return {
                tickdata_type: 'News',
                intraTicksortValue: tick.id
            };
        case 'PrivateOrder':
            return {
                tickdata_type: 'PrivateOrder',
                intraTicksortValue: `${tick.exec_id};${tick.id}`
            };
        case 'PrivateTrade':
            return {
                tickdata_type: 'PrivateTrade',
                intraTicksortValue: `${tick.trade_id};${tick.order_id};${tick.exec_id}`
            };
        case 'PrivateTradeReport':
            return {
                tickdata_type: 'PrivateTradeReport',
                intraTicksortValue: `${tick.id};${tick.status}`
            };
        default:
            return null;
    }
}

const getIntraTickSortValue = (tick: TickData): IIntraTickSortObject | null => {
    if (tick.tickdata_type === 'Alert') {
        const fallBack: IIntraTickSortObject = {
            tickdata_type: 'Alert',
            intraTicksortValue: tick.ad_id?.toString() ?? null
        };
        const causeType = getCauseTypeOfAlert(tick);
        // Order
        if (causeType === 'Order') {
            const orderId = tick.order_id;
            if (orderId === null) return fallBack;
            return {
                tickdata_type: 'Order',
                intraTicksortValue: orderId?.toString() ?? null
            }
        }
        // Trade
        else if (causeType === 'Trade') {
            const tradeNumber = tick.trade_id;
            if (tradeNumber === null) return fallBack;
            return {
                tickdata_type: 'Trade',
                intraTicksortValue: tradeNumber?.toString() ?? null
            }
        }
        // Private Order
        else if (causeType === 'PrivateOrder') {
            const privateOrderRef = tick.private_order_id;
            if (privateOrderRef === null) return fallBack;
            return {
                tickdata_type: 'PrivateOrder',
                intraTicksortValue: privateOrderRef
            }
        }
        // Private Trade
        else if (causeType === 'PrivateTrade') {
            const privateTradeRef = tick.private_trade_id;
            if (privateTradeRef === null) return fallBack;
            return {
                tickdata_type: 'PrivateTrade',
                intraTicksortValue: privateTradeRef
            };
        }
        // Private Trade Report
        else if (causeType === 'PrivateTradeReport') {
            const privateTradeReportRef = tick.private_trade_report_id;
            if (privateTradeReportRef === null) return fallBack;
            return {
                tickdata_type: 'PrivateTradeReport',
                intraTicksortValue: privateTradeReportRef
            };
        }
        return {
            tickdata_type: 'Alert',
            intraTicksortValue: null
        }
    }
    return getTickSortValue(tick);
}

interface ITickdataTypeObject {
    tickdata_type: "Order" | "Trade" | "Alert" | "News" | "Bookmark" | "PrivateOrder" | "PrivateTrade" | "PrivateTradeReport" | "Order By Level",
}

const getValueFromTickType = <T extends ITickdataTypeObject>(tick: T) => {
    switch(tick.tickdata_type) {
        // Bookmark:
        case 'Bookmark':
            return 0;
        // Order
        case 'Order':
        case 'Order By Level':
            return 1;
        // Trade
        case 'Trade':
            return 2;
        // News
        case 'News':
            return 3;
        // Private Order
        case 'PrivateOrder':
            return 4;
        // Private Trade
        case 'PrivateTrade':
            return 5;
        // Private Trade Report
        case 'PrivateTradeReport':
            return 6;
        // Alert
        case 'Alert':
            return 7;
        default:
            return 10;
    }
}

// TICK DATA SORTING FUNCTION + HELPERS
const sortByTickType = <T extends TickData>(tick1: T, tick2: T) => {
    const intraTick1 = getIntraTickSortValue(tick1);
    const intraTick2 = getIntraTickSortValue(tick2);
    
    if (intraTick1 === null && intraTick2 === null) return 0;
    if (intraTick1 === null) return -1;
    if (intraTick2 === null) return 1;
    

    const tick1Value = getValueFromTickType(intraTick1);
    const tick2Value = getValueFromTickType(intraTick2);
    const sortValue = tick1Value - tick2Value;

    if (sortValue === 0) {
        const PADDED = ['Bookmark','Order','Order By Level','Trade'];
        let intraTickSortValue1 = intraTick1.intraTicksortValue;
        let intraTickSortValue2 = intraTick2.intraTicksortValue;
        if (intraTickSortValue1 === null && intraTickSortValue2 === null) return 0;
        if (intraTickSortValue1 === null) return -1;
        if (intraTickSortValue2 === null) return 1;
        if (PADDED.includes(intraTick1.tickdata_type)) {
            const maxLength = Math.max(intraTickSortValue1.length, intraTickSortValue2.length);
            intraTickSortValue1 = intraTickSortValue1.toString().padStart(maxLength, '0');
            intraTickSortValue2 = intraTickSortValue2.toString().padStart(maxLength, '0');
        }
        
        if ((intraTick1.tickdata_type !== 'PrivateOrder' && intraTick1.tickdata_type !== 'PrivateTrade') && 
            (intraTick2.tickdata_type !== 'PrivateOrder' && intraTick2.tickdata_type !== 'PrivateTrade')) {
            if (intraTickSortValue1 < intraTickSortValue2) return -1;
            if (intraTickSortValue1 > intraTickSortValue2) return 1;
        }

        if (tick1.tickdata_type === "Order" && tick2.tickdata_type === "Order") {
            const orderTick1 = tick1 as PublicOrderTickData;
            const orderTick2 = tick2 as PublicOrderTickData;
            if (orderTick1.update_code === "Insert") return -1;
            if (orderTick1.update_code === "Deleted") return 1;
            if (orderTick2.update_code === "Insert") return 1;
            if (orderTick2.update_code === "Deleted") return -1;

            return 0;
        }

        if (tick1.tickdata_type === "PrivateOrder" && tick2.tickdata_type === "PrivateOrder") {
            const privateOrderTick1 = tick1 as IPrivateOrder;
            const privateOrderTick2 = tick2 as IPrivateOrder;
            if (privateOrderTick1.status === "new") return -1;
            if (privateOrderTick1.status !== "new") return 1;
            if (privateOrderTick2.status === "new") return 1;
            if (privateOrderTick2.status !== "new") return -1;

            return 0;
        }
        return 0;
    }
    return sortValue;
}

interface ITimestampObject {
    timestamp: string | null | undefined;
}
export const timestampObjectCompareFunction = (a: ITimestampObject, b: ITimestampObject) => {
    const isNullOrUndefined = (a: any): a is null | undefined => (a === null || a === undefined);
    if (isNullOrUndefined(a.timestamp) && isNullOrUndefined(b.timestamp)) return 0;
    if (isNullOrUndefined(a.timestamp)) return -1;
    if (isNullOrUndefined(b.timestamp)) return 1;
    if (new Date(a.timestamp) < new Date(b.timestamp)) return -1;
    if (new Date(a.timestamp) > new Date(b.timestamp)) return 1;
    return 0;
}

export const tickDataCompareFunction = <T extends TickData>(tick1: T, tick2: T) => {
    if (tick1.timestamp === null && tick2.timestamp === null) return sortByTickType(tick1, tick2);
    if (tick1.timestamp === null) return -1;
    if (tick2.timestamp === null) return 1;
    if (new Date(tick1.timestamp) < new Date(tick2.timestamp)) return -1;
    if (new Date(tick1.timestamp) > new Date(tick2.timestamp)) return 1;
    return sortByTickType(tick1, tick2);
}

export const sortTickData = <T extends TickData>(tickdata: T[]) => {
    return [...tickdata].sort(tickDataCompareFunction);
}

export const sortTickDataWithBook = <T extends WithBook<TickData>>(tickdata: T[]) => {
    return [...tickdata].sort(({ tick: tick1 }, { tick: tick2 }) => {
        if (tick1.timestamp === null && tick2.timestamp === null) return sortByTickType(tick1, tick2);
        if (tick1.timestamp === null) return -1;
        if (tick2.timestamp === null) return 1;
        if (new Date(tick1.timestamp) < new Date(tick2.timestamp)) return -1;
        if (new Date(tick1.timestamp) > new Date(tick2.timestamp)) return 1;
        return sortByTickType(tick1, tick2);
    });
}

export const sortTickOrBookData = <T extends TickData, U extends WithBook<T>>(tickdata: Array<T | U>) => {
    return [...tickdata].sort((tickOrBook1, tickOrBook2) => {
        const tick1 = isWithBookObject(tickOrBook1) ? tickOrBook1.tick : tickOrBook1;
        const tick2 = isWithBookObject(tickOrBook2) ? tickOrBook2.tick : tickOrBook2;
        if (tick1.timestamp === null && tick2.timestamp === null) return sortByTickType(tick1, tick2);
        if (tick1.timestamp === null) return -1;
        if (tick2.timestamp === null) return 1;
        if (new Date(tick1.timestamp) < new Date(tick2.timestamp)) return -1;
        if (new Date(tick1.timestamp) > new Date(tick2.timestamp)) return 1;
        return sortByTickType(tick1, tick2);
    });
}

function sortTickOrBookDataByProperty<T extends TickData, U extends WithBook<T>, P extends 'timestamp' | string>(tickdata: Array<T>, property: P, reverseSort: boolean): Array<T>
function sortTickOrBookDataByProperty<T extends TickData, U extends WithBook<T>, P extends 'timestamp' | string>(tickdata: Array<U>, property: P, reverseSort: boolean): Array<U>
function sortTickOrBookDataByProperty<T extends TickData, U extends WithBook<T>, P extends 'timestamp' | string>(tickdata: Array<T | U>, property: P, reverseSort: boolean) {
    const reversable = (sortNum: number) => (reverseSort) ? -sortNum : sortNum;
    return [...tickdata].sort((tickOrObj1, tickOrObj2) => {
        const tick1 = isWithBookObject(tickOrObj1) ? tickOrObj1.tick : tickOrObj1;
        const tick2 = isWithBookObject(tickOrObj2) ? tickOrObj2.tick : tickOrObj2;
        // Extract sortable property.
        const tick1Property = (tick1 as any)?.[property] ?? null;
        const tick2Property = (tick2 as any)?.[property] ?? null;
        // Null checks.
        if (tick1Property === null && tick2Property === null) return reversable(sortByTickType(tick1, tick2));
        if (tick1Property === null) return reversable(-1);
        if (tick2Property === null) return reversable(1);
        // On timstamp sort, convert to Date first.
        if (property === 'timestamp') {
            if (new Date(tick1Property) < new Date(tick2Property)) return reversable(-1);
            if (new Date(tick1Property) > new Date(tick2Property)) return reversable(1);
        }
        // Otherwise, sort as normal.
        else {
            if (tick1Property < tick2Property) return reversable(-1);
            if (tick1Property > tick2Property) return reversable(1);
            // After sorting by property, sort by timestamp afterwards.
            if (tick1.timestamp === null && tick2.timestamp === null) return reversable(sortByTickType(tick1, tick2));
            if (tick1.timestamp === null) return reversable(-1);
            if (tick2.timestamp === null) return reversable(1);
            if (new Date(tick1.timestamp) < new Date(tick2.timestamp)) return reversable(-1);
            if (new Date(tick1.timestamp) > new Date(tick2.timestamp)) return reversable(1);
        }
        return reversable(sortByTickType(tick1, tick2));
    });
}
export { sortTickOrBookDataByProperty };

function sortTimestampedArrayByProperty<T extends object & { timestamp: string | null }, U extends 'timestamp' | string>(list: T[], property: U, reverseSort: boolean) {
    const reversable = (sortNum: number) => (reverseSort) ? -sortNum : sortNum;
    return [...list].sort((item1, item2) => {
        // Extract sortable property.
        const item1Property = (item1 as any)?.[property] || null;
        const item2Property = (item2 as any)?.[property] || null;
        // Null checks.
        if (item1Property === null && item2Property === null) {
            if (item1.timestamp === null && item2.timestamp === null) return 0;
            if (item1.timestamp === null) return reversable(-1);
            if (item2.timestamp === null) return reversable(1);
            if (new Date(item1.timestamp) < new Date(item2.timestamp)) return reversable(-1);
            if (new Date(item1.timestamp) > new Date(item2.timestamp)) return reversable(1);
            return 0;
        }
        if (item1Property === null) return reversable(-1);
        if (item2Property === null) return reversable(1);
        // On timstamp sort, convert to Date first.
        if (property === 'timestamp') {
            if (new Date(item1Property) < new Date(item2Property)) return reversable(-1);
            if (new Date(item1Property) > new Date(item2Property)) return reversable(1);
        }
        // Otherwise, sort as normal.
        else {
            if (item1Property < item2Property) return reversable(-1);
            if (item1Property > item2Property) return reversable(1);
            // After sorting by property, sort by timestamp afterwards.
            if (item1.timestamp === null && item2.timestamp === null) return 0;
            if (item1.timestamp === null) return reversable(-1);
            if (item2.timestamp === null) return reversable(1);
            if (new Date(item1.timestamp) < new Date(item2.timestamp)) return reversable(-1);
            if (new Date(item1.timestamp) > new Date(item2.timestamp)) return reversable(1);
        }
        return 0;
    });
}
export { sortTimestampedArrayByProperty };

export const alertStatusCompareFunction = (a: AlertStatus | null, b: AlertStatus | null) => {
    const getValueFromStatus = (status: AlertStatus | null) => {
        switch (status) {
            case 'forreview':
            case null:
                return 0;
            case 'open':
                return 1;
            case 'watch':
                return 2;
            case 'investig':
                return 3;
            case 'critical':
                return 4;
            case 'explained':
                return 5;
            case 'closed':
                return 6;
            default:
                return 10;
        }
    }
    const aValue = getValueFromStatus(a);
    const bValue = getValueFromStatus(b);
    return aValue - bValue;
}
